import classNames from 'classnames'
import { useField } from 'react-final-form'
import { FormFieldProps } from '../../../../types/commonTypes'
import { ToggleSwitch, ToggleSwitchProps } from '../../ToggleSwitch/ToggleSwitch'
import { ERROR_ROUNDED_OUTLINE_CLASS_NAME, FormFieldWithError } from '../FormFieldError/FormFieldWithError'
import styles from './ToggleFormField.module.css'

type ToggleFormFieldProps = Partial<ToggleSwitchProps> & {
  description?: string | JSX.Element
  descriptionChecked?: string | JSX.Element
}

export const ToggleFormField: React.FC<FormFieldProps<ToggleFormFieldProps>> = ({
  name,
  label,
  srOnlyLabel,
  id,
  fieldContainerClassName,
  validateOnlyIfDirty,
  description,
  descriptionChecked,
  defaultChecked,
  errorClassName,
  controlWithLabelClassName,
  ...rest
}) => {
  const { input } = useField(name, { type: 'checkbox' })

  // Normalize ToggleSwitch onChange to always produce a boolean and also update RFF's field
  const { onChange: externalOnChange, ...restProps } = rest as { onChange?: (nextChecked: boolean) => void }
  const handleChange = (arg: unknown) => {
    let nextChecked: boolean
    if (typeof arg === 'boolean') {
      nextChecked = arg
    } else if (arg && typeof arg === 'object' && 'target' in (arg as any)) {
      nextChecked = !!(arg as any).target?.checked
    } else {
      nextChecked = !input.checked
    }
    // Update react-final-form's value (supports both event and boolean)
    if (typeof arg === 'object') {
      // pass through event if present to keep native semantics
      input.onChange(arg)
    } else {
      input.onChange(nextChecked)
    }
    // Call the external onChange (mutator) with normalized boolean
    externalOnChange?.(nextChecked)
  }

  return (
    <FormFieldWithError
      name={name}
      errorClassName={classNames(styles.error, errorClassName)}
      wrapperClassName={fieldContainerClassName}
      validateOnlyIfDirty={validateOnlyIfDirty}
    >
      <div
        className={classNames(
          ERROR_ROUNDED_OUTLINE_CLASS_NAME,
          styles.controlWithLabelContainer,
          controlWithLabelClassName,
        )}
      >
        <ToggleSwitch
          id={id ?? name}
          className={styles.toggle}
          name={input.name}
          checked={!!input.checked}
          onChange={handleChange}
          {...restProps}
        />
        <label htmlFor={id ?? name} className={srOnlyLabel ? 'sr-only' : styles.labelContainer}>
          {label}
          <span className={styles.description}>{input.checked ? descriptionChecked ?? description : description}</span>
        </label>
      </div>
    </FormFieldWithError>
  )
}
